import { useRouter } from "expo-router";
import { FC } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Study } from "@/types/resources";
import { UseCase, getUseCaseSlug } from "@/types/utils";

import CreateStudy from "./CreateStudy";

interface CreateStudyDialogProps {
  /**
   * Convenience for the quick-insights flow: seeds the study name as
   * `${defaultNameLabel} ${today}` (e.g. "Ad creative 07/07"). Ignored when
   * `initialName` is provided.
   */
  defaultNameLabel?: string;
  folderId?: string | null;
  initialDescription?: string;
  initialName?: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Runs after the study is created instead of the built-in navigation. Use it
   * to control where to go next (the dialog is closed before it runs).
   */
  onCreated?: (study: Study) => void;
  /** Set to duplicate an existing study rather than create a blank one. */
  studyId?: string;
  /** Set for a quick-insights study (forces `quantitative`, sends `use_case`). */
  useCase?: UseCase;
}

/**
 * Wraps the {@link CreateStudy} form in a dialog. Used both by the
 * quick-insights "Get started" flow and by study duplication.
 *
 * By default, once the study is created the dialog navigates to it:
 *  - quick-insights (`useCase` set) → the new study's audience tab,
 *  - otherwise → the new study's detail page.
 *
 * Pass `onCreated` to override the navigation.
 */
const CreateStudyDialog: FC<CreateStudyDialogProps> = ({
  defaultNameLabel,
  folderId,
  initialDescription,
  initialName,
  isOpen,
  onClose,
  onCreated,
  studyId,
  useCase,
}) => {
  const { locale } = useLocalization();
  const router = useRouter();

  const date = new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
  }).format(new Date());

  const resolvedInitialName =
    initialName ?? (defaultNameLabel ? `${defaultNameLabel} ${date}` : "");

  const handleCreated = (study: Study) => {
    onClose();
    if (onCreated) {
      onCreated(study);
      return;
    }
    router.push(
      useCase
        ? `/(dashboard)/quick-insights/${getUseCaseSlug(useCase)}/${study.id}?tab=audience`
        : `/(dashboard)/studies/${study.id}`
    );
  };

  return (
    <AlertDialog isOpen={isOpen} onClose={onClose}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="max-w-4xl">
        <AlertDialogBody className="p-2">
          {isOpen && (
            <CreateStudy
              className="border-0 p-0"
              folderId={folderId}
              initialDescription={initialDescription}
              initialName={resolvedInitialName}
              onCancel={onClose}
              onCreated={handleCreated}
              studyId={studyId}
              useCase={useCase}
            />
          )}
        </AlertDialogBody>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default CreateStudyDialog;
